import { CXoneAuth } from '@nice-devone/auth-sdk';
import { CXoneConfiguration } from '@nice-devone/common-sdk';
import { logger } from '../../../utils/logger';
import { CXONE_HOSTNAME } from '../constants';
import { decodeJwtPayload, extractUserInfoFromJwt } from '../client-helpers';
import { cxoneSdkState } from '../state';
import { buildAuthToken } from './sdk-auth-token.util';
import { initializeSdkServices, updateSdkAuthToken } from './sdk-services-init.util';

export async function initializeCXoneSDK(accessToken: string): Promise<void> {
  logger.lifecycle('[CXone] SDK initialization started');

  try {
    const cxoneAuth = CXoneAuth.instance;

    const jwtPayload = decodeJwtPayload(accessToken);
    if (!jwtPayload) {
      throw new Error('Failed to decode access token');
    }

    const tenantId = jwtPayload.tenantId as string;
    logger.debug('[CXone SDK] Tenant ID from token:', tenantId);
    logger.debug('[CXone SDK] Agent ID from token:', jwtPayload.icAgentId);

    logger.debug('[CXone SDK] Fetching CXone configuration...');
    const cxoneConfig = await cxoneAuth.getCXoneConfiguration(CXONE_HOSTNAME, tenantId, true);

    if ('errorCode' in cxoneConfig) {
      throw new Error(
        `Failed to get CXone config: ${(cxoneConfig as { errorCode: string }).errorCode}`
      );
    }

    cxoneSdkState.storedCxoneConfig = cxoneConfig as CXoneConfiguration;
    cxoneSdkState.storedUserInfo = extractUserInfoFromJwt(jwtPayload);

    logger.debug('[CXone SDK] User info extracted:', {
      icAgentId: cxoneSdkState.storedUserInfo.icAgentId,
      userName: cxoneSdkState.storedUserInfo.userName,
      tenantId: cxoneSdkState.storedUserInfo.tenantId,
    });

    const authToken = buildAuthToken(accessToken);

    logger.debug('[CXone SDK] Auth and config set, initializing ACDSessionManager...');
    await initializeSdkServices(
      accessToken,
      cxoneSdkState.storedCxoneConfig,
      authToken,
      cxoneSdkState.storedUserInfo
    );

    logger.lifecycle('[CXone] SDK initialization complete');
  } catch (error) {
    logger.error('[CXone] SDK initialization failed', error);
    throw error;
  }
}

export async function updateAccessToken(accessToken: string): Promise<void> {
  logger.lifecycle('[CXone] Access token update started');

  try {
    await updateSdkAuthToken(accessToken);
    logger.lifecycle('[CXone] Access token update complete');
  } catch (error) {
    logger.error('[CXone] Access token update failed', error);
    throw error;
  }
}
